import { Divider } from '@mui/material'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorBox } from 'components/Box'
import { BoxForm, Form, SaveButton, SelectAutocomplete } from 'components/Form'
import { CountryCode } from 'features/globalSetup/types/country'
import { CurrencyCode } from 'features/globalSetup/types/currency'
import { useAbility } from 'providers/casl'
import { SetupRetargetingDeliveryItemsContext } from '../context'
import { useGetCountryCodesOptions } from '../hooks/useGetCountryCodesOptions'
import { useGetCurrencyCodesOptions } from '../hooks/useGetCurrencyCodesOptions'
import { GamOrderRetargetingDealsDeliverySlice } from '../hooks/useGetGamOrderRetargetingDealsDelivery'
import { useUpdateGamOrderRetargetingDealsDelivery } from '../hooks/useUpdateGamOrderRetargetingDealsDelivery'
import { Schema, schema, schemaDefault } from '../schema'
import { ProposalItemsForm } from './ProposalItemsForm'

type Props = {
  gamError?: GamOrderRetargetingDealsDeliverySlice['errorMessage']
  gamStatus?: GamOrderRetargetingDealsDeliverySlice['status']
}

export const SetupRetargetingDeliveryItemsForm = ({ gamError, gamStatus }: Props): JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupRetargetingDeliveryItems.form',
  })
  const { formId, gamOrderRetargetingDealsDelivery, setTouched, setValid, touched, valid } =
    useContext(SetupRetargetingDeliveryItemsContext)
  const { errors: apiErrors, isPending, update } = useUpdateGamOrderRetargetingDealsDelivery()

  const canUpdate = ability.can(
    'update',
    'GlobalSetupFeature-googleAdManagerConnection-setupRetargetingDeliveryItems',
  )

  const { currencyCodesOptions } = useGetCurrencyCodesOptions()
  const { countryCodesOptions } = useGetCountryCodesOptions()

  return (
    <Form<Schema>
      externalErrors={apiErrors}
      id={formId}
      loading={isPending}
      onSubmit={update}
      onTouched={setTouched}
      onValid={setValid}
      schema={schema}
      schemaDefaults={schemaDefault(gamOrderRetargetingDealsDelivery)}
    >
      {({ control }) => (
        <BoxForm>
          <SelectAutocomplete<Schema, CurrencyCode>
            control={control}
            disabled={!canUpdate}
            label={t('currency.label')}
            name='currency'
            optional
            options={currencyCodesOptions}
            tooltip={{ content: t('currency.tooltip') }}
            width='sm'
          />

          <SelectAutocomplete<Schema, CountryCode>
            control={control}
            disabled={!canUpdate}
            label={t('geo.label')}
            name='geo'
            optional
            options={countryCodesOptions}
            tooltip={{ content: t('geo.tooltip') }}
            width='sm'
          />

          <Divider />
          <ProposalItemsForm canUpdate={canUpdate} />
          <Divider />

          {gamStatus === 'failed' && gamError && gamError.length > 0 && (
            <ErrorBox errorMessage={gamError} />
          )}

          <SaveButton
            can={canUpdate}
            formId={formId}
            loading={isPending || gamStatus === 'inProgress'}
            text={tc('form.action.start')}
            touched={touched}
            valid={valid}
          />
        </BoxForm>
      )}
    </Form>
  )
}
